export const normalizeFlashsalePageSource = (pageSource) => String(pageSource || "").replace(/^\/+/, "");

export const isFlashsalePageSource = (pageSource) => (
  ["flashsale", "flashdeals"].includes(normalizeFlashsalePageSource(pageSource))
);

export const getFlashsaleTierLabel = (tier) => (
  tier === "lite" ? "Premium Lite" : "Premium Pro"
);

export const getFlashsalePriceUnitText = (language = "en") => (
  language === "id" ? "/bulan" : "/month"
);

export const getFlashsaleDiscountCaption = (discount, language = "en") => (
  language === "id" ? `DISKON ${discount}%` : `SAVE ${discount}%`
);

export const getFlashsaleCtaLabel = (language = "en") => (
  language === "id" ? "Pilih Program Ini" : "Choose This Plan"
);

export const getFlashsaleTabLabel = ({ durationAmount, language = "en" }) => {
  const duration = Number(durationAmount) || 1;
  if (language === "id") {
    return `Paket ${duration} Bulan`;
  }
  const monthLabel = duration === 1 ? "Month" : "Months";
  return `${duration} ${monthLabel} Plan`;
};

export const getFlashsaleExtraLabel = (language = "en") => (
  language === "id" ? "Ekstra 28%" : "Extra 28%"
);

const getPremiumCardBaseFeatures = (language = "en") => (
  language === "id"
    ? [
      "Program Olahraga Khusus",
      "150+ Variasi Olahraga",
      "250+ Resep Sehat & Mudah",
      "100+ Audio Relaksasi & Tidur",
    ]
    : [
      "Personalized Workout Plans",
      "150+ Workout Variations",
      "250+ Easy Healthy Recipes",
      "100+ Relaxation & Sleep Audio",
    ]
);

const getPremiumCardAiFeatures = (language = "en") => (
  language === "id"
    ? [
      "Konsultasi dengan AI Coach",
      "AI Scanner Makanan & Lemak Tubuh",
    ]
    : [
      "AI Coach Consultation",
      "AI Food & Body Fat Scanner",
    ]
);

export const getFlashsaleFeatures = (tier, language = "en") => {
  const baseFeatures = getPremiumCardBaseFeatures(language);
  const aiFeatures = getPremiumCardAiFeatures(language);

  if (tier === "lite") {
    return [
      ...baseFeatures,
      ...aiFeatures.map((text) => ({ text, excluded: true })),
    ];
  }

  return [
    ...baseFeatures,
    ...aiFeatures,
  ];
};

export const getCollabPackageFeatures = (tier, language = "en") => {
  const baseFeatures = getPremiumCardBaseFeatures(language);
  const aiFeatures = getPremiumCardAiFeatures(language);

  if (tier === "lite") {
    return [
      ...baseFeatures,
      ...aiFeatures.map((text) => ({ text, excluded: true })),
    ];
  }

  return [
    ...baseFeatures,
    ...aiFeatures,
  ];
};

export const getFlashsaleDurationSubscriptionLabel = (durationAmount, language = "en") => {
  const duration = Number(durationAmount) || 1;
  if (language === "id") {
    return `${duration} Bulan Langganan`;
  }
  const monthLabel = duration === 1 ? "Month" : "Months";
  return `${duration} ${monthLabel} Subscription`;
};

export const getFlashsaleCardTitleLines = ({ durationAmount, tier, language = "en" }) => ([
  getFlashsaleTierLabel(tier).toUpperCase(),
  getFlashsaleDurationSubscriptionLabel(durationAmount, language),
]);

export const getFlashsaleCheckoutName = ({ durationAmount, tier, language = "en" }) => {
  const subscriptionLabel = getFlashsaleDurationSubscriptionLabel(durationAmount, language);
  if (tier === "lite") {
    return `${subscriptionLabel} - Premium Lite`;
  }
  return subscriptionLabel;
};

export const getFlashsaleDisplayNameLines = (name) => {
  const text = String(name || "").replace(/\s+/g, " ").trim();
  const hyphenMatch = text.match(/^(.*?)\s*-\s*(Premium\s+Lite|Full\s+Premium|Premium\s+Pro)$/i);
  if (hyphenMatch) {
    return [hyphenMatch[1].trim(), hyphenMatch[2].replace(/\s+/g, " ").trim()];
  }

  const suffixMatch = text.match(/^(.*?)(Premium\s+Lite|Full\s+Premium|Premium\s+Pro)$/i);
  if (suffixMatch) {
    return [suffixMatch[1].trim(), suffixMatch[2].replace(/\s+/g, " ").trim()].filter(Boolean);
  }

  return [text].filter(Boolean);
};

export const getFlashsaleRecurringTermsText = ({ priceText, durationAmount, language = "en" }) => {
  if (!priceText) {
    return "";
  }
  const duration = Number(durationAmount) || 1;
  if (language === "id") {
    const periodText = duration === 1 ? "bulan" : `${duration} bulan`;
    return `Berikutnya kamu akan ditagih ${priceText} setiap ${periodText} sampai dibatalkan.`;
  }
  const periodText = duration === 1 ? "month" : `${duration} months`;
  return `Next you will be charged ${priceText} every ${periodText} until canceled.`;
};

// Format a numeric amount into the package currency (mirrors the checkout page fmt()).
const formatCheckoutAmount = (val, currency = "IDR") => {
  if (val == null || val === "") return null;
  const num = Number(val);
  if (!Number.isFinite(num)) return null;
  if (currency === "IDR") return `Rp${Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  if (currency === "USD") return `$${num.toFixed(2)}`;
  if (currency === "JPY") return `¥${Math.round(num).toLocaleString("ja-JP")}`;
  if (currency === "SGD") return `S$${num.toFixed(2)}`;
  return `${currency} ${num}`;
};

const parseCheckoutAmount = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (!raw) return null;
  const numericText = raw.replace(/[^0-9.,-]/g, "");
  if (!numericText || numericText === "-") return null;

  let normalized = numericText;
  const lastComma = numericText.lastIndexOf(",");
  const lastDot = numericText.lastIndexOf(".");

  if (lastComma !== -1 && lastDot !== -1) {
    normalized = lastComma > lastDot
      ? numericText.replace(/\./g, "").replace(",", ".")
      : numericText.replace(/,/g, "");
  } else if (lastDot !== -1) {
    const parts = numericText.split(".");
    normalized = parts.length > 1 && parts[parts.length - 1].length === 3
      ? parts.join("")
      : numericText;
  } else if (lastComma !== -1) {
    const parts = numericText.split(",");
    normalized = parts.length > 1 && parts[parts.length - 1].length === 3
      ? parts.join("")
      : numericText.replace(",", ".");
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const rawCollabCheckoutDisplay = (plan, tier = "full") => {
  const resolvedTier = tier === "lite" ? "lite" : "full";
  const display = plan && plan.collab_checkout_display;
  if (display && display[resolvedTier]) {
    return display[resolvedTier];
  }
  if (display && (
    display.name
    || display.total_price_text
    || display.terms_text
    || display.left_title
  )) {
    return display;
  }
  return {};
};

const getCollabPricing = (plan) => (
  (plan && (plan.collab_pricing || plan.flashsale_pricing)) || {}
);

export const getCollabFirstCycleAmount = (plan, tier = "full") => {
  const display = rawCollabCheckoutDisplay(plan, tier);
  const displayAmount = parseCheckoutAmount(display && display.total_price_text);
  if (displayAmount !== null) return displayAmount;

  const trialAmount = parseCheckoutAmount(plan && plan.collab_trial_price);
  if (trialAmount !== null) return trialAmount;

  const pricing = getCollabPricing(plan);
  if (Object.prototype.hasOwnProperty.call(pricing, "first_price")) {
    return parseCheckoutAmount(pricing.first_price);
  }

  return null;
};

export const getCollabFirstCyclePriceText = (plan, tier = "full") => {
  const display = rawCollabCheckoutDisplay(plan, tier);
  if (display && display.total_price_text) return display.total_price_text;

  const amount = getCollabFirstCycleAmount(plan, tier);
  const currency = (plan && plan.currency_code) || "IDR";
  return amount !== null ? formatCheckoutAmount(amount, currency) : "";
};

export const getCollabRetailAmount = (plan, tier = "full") => {
  if (!plan) return null;
  if (tier === "lite") {
    const liteRetail = parseCheckoutAmount(plan.fs_pl_retail_price);
    if (liteRetail !== null && liteRetail > 0) return liteRetail;
  }

  const retailText = parseCheckoutAmount(plan.retail_price);
  if (retailText !== null && retailText > 0) return retailText;

  return parseCheckoutAmount(plan.price);
};

export const getCollabTrialDiscountPercentage = (plan, tier = "full") => {
  const firstCycleAmount = getCollabFirstCycleAmount(plan, tier);
  const retailAmount = getCollabRetailAmount(plan, tier);

  if (firstCycleAmount === null || retailAmount === null || retailAmount <= 0) {
    return null;
  }

  const discountAmount = Math.max(0, retailAmount - firstCycleAmount);
  return Math.round((discountAmount / retailAmount) * 100);
};

// Resolve the package duration in months from a checkout-detail payload, which
// does NOT expose validity_period_amount. Falls back to parsing price_text_period
// ("Rp129.000 / 2 Months") or the package name ("2 Bulan Langganan").
export const getCheckoutDurationAmount = (content = {}) => {
  const direct = Number(content.validity_period_amount);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const period = String(content.price_text_period || "");
  const periodMatch = period.match(/\/\s*(\d+)\s*(?:month|bulan)/i);
  if (periodMatch) return Number(periodMatch[1]);
  const name = String(content.name || "");
  const nameMatch = name.match(/(\d+)\s*(?:bulan|month)/i);
  if (nameMatch) return Number(nameMatch[1]);
  return 1;
};

// Build the recurring "next charged" terms text for a flashsale checkout from the
// package detail payload, using the correct package duration and the selected
// language. The backend returns this string English-only with an unreliable
// cadence, so the checkout page rebuilds it from structured fields.
export const buildFlashsaleCheckoutRecurringTerms = (content = {}, language = "en") => {
  if (!content) return "";
  const currency = content.currency_code || "IDR";
  const flashsalePricing = content.flashsale_pricing || {};
  const recurringRaw = flashsalePricing.recurring_price != null
    ? flashsalePricing.recurring_price
    : content.recurring_price;
  const recurringPriceText = formatCheckoutAmount(recurringRaw, currency)
    || flashsalePricing.recurring_price_text
    || content.recurring_price_text
    || content.total_price_text
    || content.price_text
    || "";
  if (!recurringPriceText) return content.terms_text || "";
  return getFlashsaleRecurringTermsText({
    priceText: recurringPriceText,
    durationAmount: getCheckoutDurationAmount(content),
    language,
  });
};

export const getCollabCheckoutDisplay = (plan, tier = "full") => {
  const resolvedTier = tier === "lite" ? "lite" : "full";
  const display = rawCollabCheckoutDisplay(plan, resolvedTier);
  const firstCycleAmount = getCollabFirstCycleAmount(plan, resolvedTier);

  if (display.left_title && firstCycleAmount !== null && firstCycleAmount > 0) {
    return {
      ...display,
      left_title: null,
    };
  }

  return display;
};

export const getCollabDisplayNameLines = (name) => {
  const text = String(name || "").replace(/\s+/g, " ").trim();
  if (!text) {
    return [];
  }

  const liteMatch = text.match(/^(.*?)(?:\s*-\s*|\s+)?Premium\s+Lite$/i);
  if (liteMatch) {
    const subscriptionLabel = liteMatch[1].trim();
    return [subscriptionLabel, "Premium Lite"].filter(Boolean);
  }

  const fullMatch = text.match(/^(.*?)(?:\s*-\s*|\s+)?(?:Full\s+Premium|Premium\s+Pro)$/i);
  if (fullMatch) {
    const subscriptionLabel = fullMatch[1].trim();
    return [subscriptionLabel].filter(Boolean);
  }

  return text.split("\n").map((line) => line.trim()).filter(Boolean);
};

export const getCollabCmsSlug = (slugOrCmsSlug) => {
  const rawValue = String(slugOrCmsSlug || "").trim();
  if (!rawValue) {
    return "";
  }
  const normalizedValue = rawValue.replace(/^\/+/, "");
  if (normalizedValue.indexOf("c/") === 0) {
    return `/${normalizedValue}`;
  }
  return `/c/${normalizedValue}`;
};
